import { useCallback, useState } from 'react';
import ErrorIcon from '@mui/icons-material/Error';
import { Button, FormHelperText, Stack, Typography } from '@mui/material';

import { FBoxContainer } from '../../../../components/baseV2';
import FProgressBar from '../../../../components/base/FProgressBar';
import { FORTA_ERROR } from '../../../../utils/colors';
import { ConfirmDialog } from '../confirmDialog';
import { useFormReadOnly } from '../formReadOnlyProvider';
import { AutoSaveListRow } from './components';
import { useAutoSaveListDraft } from './hooks';
import { LIST_CONTAINER_SX } from '../../styles';

import type { FormikValues } from 'formik';
import type { AutoSaveListConfig, AutoSaveListDeleteConfirm } from './types';

export default function AutoSaveList<
  TValues extends FormikValues,
  TRow,
  TPayload extends object,
>(config: AutoSaveListConfig<TValues, TRow, TPayload>) {
  const {
    entries,
    getRowKey,
    getDeleteConfirm,
    onDelete,
    addButtonLabel,
    disabled: disabledProp = false,
    isSaving = false,
    requireCompleteDraft: requireCompleteDraftProp,
    autoOpenWhenEmpty = false,
    addButtonSx,
    requiredMessage,
    listErrorMessage,
    emptyReadOnlyMessage,
    plainDraft = false,
    addDisabled = false,
    addDisabledMessage,
    getDeleteBlock,
  } = config;
  const requireCompleteDraft = requireCompleteDraftProp ?? plainDraft;

  const readOnlyFromContext = useFormReadOnly();
  const disabled = disabledProp || readOnlyFromContext;
  const {
    hasDraft,
    draftSeq,
    draftValid,
    draftPersisted,
    draftCreatedId,
    setDraftValid,
    setDraftPersisted,
    setDraftCreatedId,
    savedDraftRow,
    visibleEntries,
    pendingDeleteRow,
    setPendingDeleteRow,
    isDeleting,
    openDraft,
    commitPlainDraft,
    discardDraft,
    confirmDelete,
    closeDelete,
  } = useAutoSaveListDraft({
    addDisabled,
    autoOpenWhenEmpty: autoOpenWhenEmpty || plainDraft,
    disabled,
    entries,
    getRowKey,
    onDelete,
  });
  const [deleteBlock, setDeleteBlock] = useState<AutoSaveListDeleteConfirm | null>(null);

  const showRequiredError = Boolean(requiredMessage) && !hasDraft && entries.length === 0;
  const addDisabledWhileDrafting =
    hasDraft &&
    (draftCreatedId == null || !draftPersisted || (requireCompleteDraft && !draftValid));
  const handleAdd = plainDraft && hasDraft ? commitPlainDraft : openDraft;
  const deleteConfirm = pendingDeleteRow ? getDeleteConfirm(pendingDeleteRow) : null;
  const handleRequestDelete = useCallback(
    (row: TRow) => {
      const block = getDeleteBlock?.(row);
      if (block) {
        setDeleteBlock(block);
        return;
      }
      setPendingDeleteRow(row);
    },
    [getDeleteBlock, setPendingDeleteRow],
  );
  const closeDeleteBlock = useCallback(() => setDeleteBlock(null), []);

  return (
    <FBoxContainer sx={LIST_CONTAINER_SX}>
      {isSaving ? <FProgressBar /> : null}

      <Stack gap={2}>
        {readOnlyFromContext ? null : (
          <Stack alignItems="flex-end" gap={0.5}>
            <Button
              disabled={disabled || isSaving || addDisabledWhileDrafting || addDisabled}
              onClick={handleAdd}
              startIcon={
                showRequiredError || listErrorMessage ? (
                  <ErrorIcon sx={{ color: FORTA_ERROR }} />
                ) : undefined
              }
              sx={addButtonSx}
              variant="contained"
            >
              {addButtonLabel}
            </Button>
            {addDisabled && addDisabledMessage ? (
              <Typography color="text.secondary" variant="caption">
                {addDisabledMessage}
              </Typography>
            ) : null}
          </Stack>
        )}

        {hasDraft || visibleEntries.length > 0 ? (
          <Stack>
            {hasDraft && !(addDisabled && draftCreatedId == null && draftPersisted) ? (
              <AutoSaveListRow
                key={`draft-${draftSeq}`}
                config={config}
                disabled={disabled}
                index={0}
                onValidityChange={setDraftValid}
                onPersistedChange={setDraftPersisted}
                onCreated={setDraftCreatedId}
                onDiscardDraft={discardDraft}
                onRequestDelete={handleRequestDelete}
                row={null}
                savedRow={savedDraftRow}
              />
            ) : null}

            {visibleEntries.map((row, entryIndex) => (
              <AutoSaveListRow
                key={getRowKey(row)}
                config={config}
                disabled={disabled}
                index={hasDraft && !plainDraft ? entryIndex + 1 : entryIndex}
                onCreated={setDraftCreatedId}
                onDiscardDraft={discardDraft}
                onRequestDelete={handleRequestDelete}
                row={row}
                savedRow={null}
              />
            ))}
          </Stack>
        ) : null}

        {readOnlyFromContext && emptyReadOnlyMessage && entries.length === 0 ? (
          <Typography color="text.secondary">{emptyReadOnlyMessage}</Typography>
        ) : null}

        {showRequiredError ? <FormHelperText error>{requiredMessage}</FormHelperText> : null}

        {listErrorMessage ? <FormHelperText error>{listErrorMessage}</FormHelperText> : null}
      </Stack>

      <ConfirmDialog
        open={pendingDeleteRow != null}
        title={deleteConfirm?.title ?? ''}
        message={deleteConfirm?.message ?? ''}
        confirmLabel={deleteConfirm?.confirmLabel ?? 'Delete'}
        confirmLoading={isDeleting}
        onClose={closeDelete}
        onConfirm={confirmDelete}
      />

      <ConfirmDialog
        open={deleteBlock != null}
        title={deleteBlock?.title ?? ''}
        message={deleteBlock?.message ?? ''}
        confirmLabel={deleteBlock?.confirmLabel ?? 'OK'}
        hideCancel
        onClose={closeDeleteBlock}
        onConfirm={closeDeleteBlock}
      />
    </FBoxContainer>
  );
}
